import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button as CrcButton } from '@crc-evans/ui-components';
import {
  calculateTotalCost,
  collectBackupEquipment,
  collectSelectedEquipment,
} from '../../utils/excelGenerator';
import './MobilistDetailView.css';

// ── Equipment data (CCAB section) ──────────────────────────────
// ALL equipment is picked ONCE for the full SOW (scope of work).
// The same equipment is shared across every pipe/line, so there is
// a single selection — nothing is repeated, charged or mobilised
// per line.
const EQUIPMENT_CATEGORIES = [
  {
    id: 'ccab',
    label: 'CCAB',
    groups: [
      {
        name: '1060CFM Compressor',
        items: ['CRCE Supply', 'Client Supply'],
      },
      {
        name: 'Desiccant Dryer',
        items: [
          'CRCE Supply',
          'Client Supply',
          'Generic',
          'Desiccant Dryer (DH MXP107)',
          'Desiccant Dryer (SD60)',
        ],
      },
      {
        name: 'Closed Cycle Blaster',
        items: [
          'Generic',
          'Closed Cycle Blaster (Nederman)',
          'Closed Cycle Blaster (LTC)',
        ],
      },
      {
        name: 'Blast Frame - Manual',
        items: [
          'Blast Frame 2" - 6"',
          'Blast Frame 6" - 12"',
          'Blast Frame 12" - 18"',
          'Blast Frame 18" - 24"',
          'Blast Frame 24" - 30"',
        ],
      },
      {
        name: 'Blast Frame - Semi Auto',
        items: [
          'Blast Frame 12" - 18"',
          'Blast Frame 18" - 24"',
          'Blast Frame 24" - 30"',
        ],
      },
    ],
  },
];

// ── Steel Cutbacks formatter ────────────────────────────────────
// Handles an array of plain strings (one per input box, e.g.
// "350/175 +/-10"), the pre-formatted text, the earlier object shape
// ({primary, secondary, toleranceSign, tolerance}), and a legacy
// single scalar value.
const formatSteelCutbacks = (pipeline) => {
  if (!pipeline) return '';
  if (pipeline.steelCutbacksText) return pipeline.steelCutbacksText;
  const value = pipeline.steelCutbacks;
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (!entry) return '';
        if (typeof entry !== 'object') return String(entry);
        if ('value' in entry) {
          if (!String(entry.value || '').trim()) return '';
          const value = String(entry.value).trim();
          const plus = entry.tolerancePlus ?? (entry.toleranceSign === '-' ? '' : entry.tolerance) ?? '';
          const minus = entry.toleranceMinus ?? (entry.toleranceSign === '+' ? '' : entry.tolerance) ?? '';
          if (plus && minus) return `${value} (+${plus} / -${minus})`;
          if (plus) return `${value} +${plus}`;
          if (minus) return `${value} -${minus}`;
          return value;
        }
        if (entry.primary === '' && entry.secondary === '') return '';
        const sizes = entry.secondary ? `${entry.primary}/${entry.secondary}` : `${entry.primary}`;
        return entry.tolerance !== '' && entry.tolerance != null
          ? `${sizes} ${entry.toleranceSign || '±'}${entry.tolerance}`
          : sizes;
      })
      .filter(Boolean)
      .join(', ');
  }
  return value ? String(value) : '';
};

// ── Date formatter ──────────────────────────────────────────────
const formatDate = (date) => {
  const d = new Date(date);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

// ── Saved-selection normalisers (for View / Edit) ───────────────
// Converts a saved selections value into { shared: {catId: {group: Set}} }.
// Accepts the current shared shape, and the legacy per-line shape
// ({[lineIdx]: {catId: ...}}), merging every line into the one SOW set.
const normalizeSelections = (raw) => {
  const shared = {};
  const mergeCategories = (categories) => {
    for (const [catId, groups] of Object.entries(categories || {})) {
      for (const [groupName, items] of Object.entries(groups || {})) {
        const set = new Set(shared?.[catId]?.[groupName] || []);
        (Array.isArray(items) ? items : Array.from(items || [])).forEach((item) => set.add(item));
        shared[catId] = { ...(shared[catId] || {}), [groupName]: set };
      }
    }
  };
  if (!raw) return { shared };
  if (raw.shared || raw.lines) {
    mergeCategories(raw.shared);
    Object.values(raw.lines || {}).forEach(mergeCategories);
  } else {
    Object.values(raw).forEach(mergeCategories);
  }
  return { shared };
};

const normalizeBackupSelections = (raw) => {
  if (!raw) return new Set();
  if (Array.isArray(raw)) return new Set(raw);
  if (raw instanceof Set) return new Set(raw);
  return new Set(Object.keys(raw).filter((key) => raw[key]));
};

// ── Component ────────────────────────────────────────────────────
const MobilistDetailView = ({ data, onBack, onSave, readOnly = false, saveLabel = 'Save Mobilist', saveDisabled = false }) => {
  const [activeCategory, setActiveCategory] = useState('ccab');
  // ONE equipment selection for the entire SOW, shared by all lines:
  //   selections.shared[catId][groupName] = Set of items
  // Pre-filled from the saved mobilist when viewing / editing.
  const [selections, setSelections] = useState(() => normalizeSelections(data.selections));

  // Backup equipment checkbox — ONE per equipment section/group
  // (e.g. one for 1060CFM Compressor, one for Desiccant Dryer).
  // The selected items of a ticked section are saved with the
  // mobilist and exported to Excel under BACK UP EQUIPMENT (as an
  // extra, costed copy of the plant equipment).
  // Keys are `${catId}|${groupName}`.
  const [backupSelections, setBackupSelections] = useState(() => normalizeBackupSelections(data.backupSelections));

  const isBackupChecked = (catId, groupName) =>
    backupSelections.has(`${catId}|${groupName}`);

  const toggleBackup = (catId, groupName) => {
    if (readOnly) return;
    const key = `${catId}|${groupName}`;
    setBackupSelections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const numPipelines = parseInt(data.numberOfPipelines, 10) || 1;
  const pipelines = Array.from({ length: numPipelines }, (_, i) => (data.pipelines || [])[i] || {});
  const today = formatDate(new Date());
  const createdDate = data.createdDate ? formatDate(data.createdDate) : today;
  const updatedDate = data.updatedDate ? formatDate(data.updatedDate) : today;

  const isChecked = (catId, groupName, itemName) =>
    selections?.shared?.[catId]?.[groupName]?.has(itemName) || false;

  const toggleItem = (catId, groupName, itemName) => {
    if (readOnly) return;
    setSelections((prev) => {
      const shared = prev.shared || {};
      const set = new Set(shared?.[catId]?.[groupName] || []);
      if (set.has(itemName)) set.delete(itemName);
      else set.add(itemName);
      return {
        ...prev,
        shared: {
          ...shared,
          [catId]: { ...(shared[catId] || {}), [groupName]: set },
        },
      };
    });
  };

  const activeCat = EQUIPMENT_CATEGORIES.find((c) => c.id === activeCategory);

  // The mobilist itself: each selected item once (qty 1) for the full SOW.
  const mobilistItems = collectSelectedEquipment(selections, numPipelines);

  // Backup equipment: selected items of sections with Backup ticked.
  // These appear in the Excel under BACK UP EQUIPMENT (qty 1, costed),
  // in addition to Plant Equipment.
  const backupItems = collectBackupEquipment(selections, backupSelections, numPipelines);

  const renderGroup = (group) => (
    <div key={group.name} className="mdv-group">
      <div className="mdv-group-title">
        <span>{group.name}</span>
        {/* One backup equipment checkbox per section */}
        <label className="mdv-backup-label" title="Backup equipment">
          <input
            type="checkbox"
            className="mdv-checkbox mdv-backup-checkbox"
            checked={isBackupChecked(activeCategory, group.name)}
            disabled={readOnly}
            onChange={() => toggleBackup(activeCategory, group.name)}
          />
          <span>Backup Equipment</span>
        </label>
      </div>
      <div className="mdv-group-items">
        {group.items.map((item) => {
          const checked = isChecked(activeCategory, group.name, item);
          return (
            <div
              key={item}
              className={`mdv-item${checked ? ' mdv-item-checked' : ''}`}
              onClick={() => toggleItem(activeCategory, group.name, item)}
            >
              <label className="mdv-item-label">
                <input
                  type="checkbox"
                  className="mdv-checkbox"
                  checked={checked}
                  disabled={readOnly}
                  onChange={() => {}}
                />
                <span>{item}</span>
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={`mdv-page${readOnly ? ' mdv-page-readonly' : ''}`}>

      {/* ── Header ── */}
      <div className="mdv-header">
        <button className="mdv-back-btn" onClick={onBack}>
          <ArrowLeft size={20} />
        </button>
        <h1 className="mdv-title">Mobilist Details</h1>
      </div>

      {/* ── Project info bar ── */}
      <div className="mdv-info-bar">
        <div className="mdv-info-item">
          <span className="mdv-info-label">Project Name</span>
          <span className="mdv-info-value">{data.projectName || '—'}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">Project No.</span>
          <span className="mdv-info-value">{data.projectNumber || '—'}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">No. of Pipelines</span>
          <span className="mdv-info-value">{numPipelines}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">Project Type</span>
          <span className="mdv-info-value">{data.projectType || '—'}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">Shifts</span>
          <span className="mdv-info-value">{data.shifts || '—'}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">Created By</span>
          <span className="mdv-info-value">{data.createdBy || '—'}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">Created Date</span>
          <span className="mdv-info-value">{createdDate}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">Updated Date</span>
          <span className="mdv-info-value">{updatedDate}</span>
        </div>
      </div>

      {/* ── SOW summary: every line, so equipment is picked for the
             full scope of work rather than line-by-line ── */}
      <div className="mdv-sow">
        <div className="mdv-sow-heading">
          <span className="mdv-sow-title">Scope of Work — All Lines ({numPipelines})</span>
          <span className="mdv-sow-note">
            Equipment below is selected once for this full SOW and shared across all lines.
          </span>
        </div>
        <div className="mdv-sow-table-wrap">
          <table className="mdv-sow-table">
            <thead>
              <tr>
                <th>Line</th>
                <th>Pipe Size</th>
                <th>Wall Thickness (mm)</th>
                <th>No. of Field Joints</th>
                <th>Factory Coating</th>
                <th>FJC Type</th>
                <th>Steel Cutbacks (mm)</th>
                <th>Infill</th>
              </tr>
            </thead>
            <tbody>
              {pipelines.map((p, i) => (
                <tr key={i}>
                  <td className="mdv-sow-line">Line {i + 1}</td>
                  <td>{p.pipeSize || '—'}</td>
                  <td>{p.wallThickness || '—'}</td>
                  <td>{p.numberOfFieldJoints || '—'}</td>
                  <td>{p.factoryCoatingType || '—'}</td>
                  <td>{p.fjcType || '—'}</td>
                  <td>{formatSteelCutbacks(p) || '—'}</td>
                  <td>
                    {[p.infillType, p.infillCutbacks && `${p.infillCutbacks}mm cutback`, p.infillThickness && `${p.infillThickness}mm thick`]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="mdv-body">

        {/* Sidebar */}
        <div className="mdv-sidebar">
          {EQUIPMENT_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              className={`mdv-sidebar-item${activeCategory === cat.id ? ' mdv-sidebar-item-active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Equipment content */}
        <div className="mdv-content">
          {activeCat && (
            <>
              {/* Equipment — one selection for the entire SOW, shared by all lines */}
              <div className="mdv-equipment-section">
                <div className="mdv-section-heading">
                  <span className="mdv-section-title">Equipment — Full SOW</span>
                  <span className="mdv-section-note">Select once — shared across all {numPipelines} line{numPipelines !== 1 ? 's' : ''}</span>
                </div>
                <div className="mdv-groups">
                  {(activeCat.groups || []).map((group) => renderGroup(group))}
                </div>
              </div>

              {/* Mobilist preview — the single SOW selection */}
              <div className="mdv-equipment-section">
                <div className="mdv-section-heading">
                  <span className="mdv-section-title">Mobilist Summary</span>
                  <span className="mdv-section-note">
                    {mobilistItems.length} item{mobilistItems.length !== 1 ? 's' : ''} for the full SOW
                  </span>
                </div>
                {mobilistItems.length === 0 ? (
                  <p className="mdv-summary-empty">No equipment selected yet.</p>
                ) : (
                  <table className="mdv-summary-table">
                    <thead>
                      <tr>
                        <th>Equipment</th>
                        <th>Type</th>
                        <th>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mobilistItems.map((entry) => (
                        <tr key={`${entry.catId}|${entry.groupName}|${entry.item}`}>
                          <td>{entry.item}</td>
                          <td>{entry.groupName}</td>
                          <td>1</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Backup equipment preview — exported under BACK UP EQUIPMENT */}
              {backupItems.length > 0 && (
                <div className="mdv-equipment-section">
                  <div className="mdv-section-heading">
                    <span className="mdv-section-title">Backup Equipment</span>
                    <span className="mdv-section-note">
                      {backupItems.length} item{backupItems.length !== 1 ? 's' : ''} — also in Plant Equipment, repeated as backup in the Excel
                    </span>
                  </div>
                  <table className="mdv-summary-table">
                    <thead>
                      <tr>
                        <th>Equipment</th>
                        <th>Type</th>
                        <th>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backupItems.map((entry) => (
                        <tr key={`backup|${entry.catId}|${entry.groupName}|${entry.item}`}>
                          <td>{entry.item}</td>
                          <td>{entry.groupName}</td>
                          <td>1</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="mdv-footer">
        {readOnly ? (
          <CrcButton label="Back" variant="secondary" onClick={onBack} size="small" />
        ) : (
          <>
            <CrcButton label="Cancel" variant="secondary" onClick={onBack} size="small" />
            <CrcButton
              label={saveLabel}
              variant="primary"
              disabled={saveDisabled}
              onClick={() => {
                if (saveDisabled) return;
                const totalCost = calculateTotalCost(selections, numPipelines, backupSelections);
                onSave({ ...data, selections, backupSelections: [...backupSelections], totalCost });
              }}
              size="small"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MobilistDetailView;
